import React from "react";
import { Box, Container, Grid, Typography, Button } from "@mui/material";
import { Code, Palette, TrendingUp } from "@mui/icons-material";
import { Card, SectionTitle, useContactDialog } from "../../../common";
import {
  colors,
  spacing,
  typography,
  borderRadius,
} from "../../../../theme/tokens";

const SERVICES = [
  {
    en: "DEVELOPMENT",
    title: "Webシステム開発",
    icon: <Code sx={{ fontSize: "1.6rem" }} />,
    desc: "要件定義から設計・実装・運用まで。規模に合った現実的な構成でつくります。",
  },
  {
    en: "DESIGN",
    title: "UI/UXデザイン",
    icon: <Palette sx={{ fontSize: "1.6rem" }} />,
    desc: "使う人の文脈から逆算した情報設計と、ブランドの世界観をつなぐビジュアルデザイン。",
  },
  {
    en: "GROWTH",
    title: "運用・グロース支援",
    icon: <TrendingUp sx={{ fontSize: "1.6rem" }} />,
    desc: "リリース後の改善サイクルを伴走。計測設計やコンテンツ運用まで支援します。",
  },
];

const FLOW = [
  {
    step: "01",
    title: "ヒアリング",
    desc: "課題と目的を丁寧にお聞きし、ゴールのイメージをすり合わせます。ラフな相談段階でも歓迎です。",
  },
  {
    step: "02",
    title: "提案・お見積り",
    desc: "実現方法を複数の選択肢で提案。過剰な機能は削ぎ落とし、身の丈に合った費用感になるよう調整してから進めます。",
  },
  {
    step: "03",
    title: "設計・制作",
    desc: "デザインと開発を小さく回しながら制作。途中経過を随時共有し、認識のズレを防ぎます。",
  },
  {
    step: "04",
    title: "公開・運用",
    desc: "リリースして終わりではなく、データを見ながら改善を継続。長く使われるプロダクトへ育てます。",
  },
];

function Service() {
  const { open: openContactDialog } = useContactDialog();

  return (
    <Box id="service" sx={{ scrollMarginTop: { xs: "72px", md: "100px" } }}>
      <Container maxWidth="lg" sx={{ py: { xs: 8, md: 12 } }}>
        <SectionTitle en="SERVICE" ja="事業内容" />
        <Grid container spacing={4} sx={{ mt: spacing[400] }}>
          {SERVICES.map((s) => (
            <Grid item xs={12} sm={6} md={4} key={s.en}>
              <Card sx={{ p: spacing[800], height: "100%" }}>
                <Box
                  sx={{
                    width: "52px",
                    height: "52px",
                    borderRadius: borderRadius.lg,
                    backgroundColor: colors.primary[50],
                    color: colors.primary[600],
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {s.icon}
                </Box>
                <Typography
                  sx={{
                    mt: spacing[500],
                    fontSize: typography.fontSize.xs,
                    fontWeight: typography.fontWeight.semibold,
                    color: colors.primary[600],
                    letterSpacing: typography.letterSpacing.widest,
                  }}
                >
                  {s.en}
                </Typography>
                <Typography
                  variant="h6"
                  sx={{
                    mt: spacing[100],
                    fontWeight: typography.fontWeight.bold,
                  }}
                >
                  {s.title}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    mt: spacing[300],
                    color: colors.neutral[600],
                    lineHeight: typography.lineHeight.relaxed,
                  }}
                >
                  {s.desc}
                </Typography>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      <Box sx={{ backgroundColor: colors.neutral[50], py: { xs: 8, md: 12 } }}>
        <Container maxWidth="lg">
          <SectionTitle en="FLOW" ja="ご依頼の流れ" />
          <Grid container spacing={4} sx={{ mt: spacing[400] }}>
            {FLOW.map((f, i) => (
              <Grid item xs={12} sm={6} md={3} key={f.step}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: spacing[300],
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: typography.fontSize["3xl"],
                      fontWeight: typography.fontWeight.extrabold,
                      color: colors.primary[200],
                      fontFamily: typography.fontFamily.mono,
                      lineHeight: 1,
                    }}
                  >
                    {f.step}
                  </Typography>
                  {i < FLOW.length - 1 && (
                    <Box
                      sx={{
                        display: { xs: "none", md: "block" },
                        flex: 1,
                        height: "2px",
                        background: `linear-gradient(90deg, ${colors.primary[200]}, ${colors.primary[50]})`,
                      }}
                    />
                  )}
                </Box>
                <Typography
                  variant="h6"
                  sx={{
                    mt: spacing[400],
                    fontWeight: typography.fontWeight.bold,
                  }}
                >
                  {f.title}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    mt: spacing[200],
                    color: colors.neutral[600],
                    lineHeight: typography.lineHeight.relaxed,
                  }}
                >
                  {f.desc}
                </Typography>
              </Grid>
            ))}
          </Grid>

          <Box sx={{ mt: spacing[1200], textAlign: "center" }}>
            <Button
              variant="contained"
              onClick={openContactDialog}
              sx={{
                backgroundColor: colors.primary[600],
                color: colors.neutral[0],
                px: spacing[800],
                py: spacing[400],
                fontSize: typography.fontSize.base,
                fontWeight: typography.fontWeight.medium,
                borderRadius: borderRadius.full,
                "&:hover": { backgroundColor: colors.primary[700] },
              }}
            >
              お問い合わせはこちら →
            </Button>
          </Box>
        </Container>
      </Box>
    </Box>
  );
}

export default Service;
